/**
 * Plain-language tooltips for every input and output of the column module.
 *
 * Wording rule: short (1-2 sentences), explains *what* it is and *what it
 * is for in this app*. Avoid academic prose. The cited norm article goes at
 * the end so it can be skimmed.
 */

export const HELP = {
  // Section
  sectionType:
    'Forma de la seccion transversal de la columna. Hoy esta disponible rectangular; L, T, cruciforme y circular se incorporan en proximas iteraciones.',
  L1: 'Ancho de la seccion en cm (eje X). Para una columna 25x40, L1 = 25.',
  L2: 'Altura de la seccion en cm (eje Y). Para una columna 25x40, L2 = 40.',

  // Materials
  fc: "Resistencia a compresion del concreto a los 28 dias (f'c). En obra peruana es comun 175, 210, 280, 350 kgf/cm2. E.060 cap. 4.",
  fy: 'Esfuerzo de fluencia del acero de refuerzo (fy). En Peru se usa Grado 60: fy = 4200 kgf/cm2. ASTM A615 / E.060 cap. 3.',
  cover: 'Recubrimiento libre desde la cara del concreto al estribo. Tipico 4 cm para columnas interiores. E.060 7.7.',

  // Loads
  Pu: 'Carga axial factorada (compresion positiva). ORIGEN: tu analisis estructural (ETABS, SAP, manual). Aplica las combinaciones de E.060 9.2 (1.4D+1.7L, 1.25(D+L)±E, 0.9D±E) y trae el caso mas critico. AlphaConcreto NO calcula este valor; solo lo verifica contra la capacidad de la seccion.',
  Mu_x:
    'Momento factorado alrededor del eje X (kgf·cm). ORIGEN: tu analisis estructural en el caso critico (sismo X dominante). NO se calcula en la app.',
  Mu_y:
    'Momento factorado alrededor del eje Y (kgf·cm). ORIGEN: tu analisis estructural (sismo Y dominante). Para flexion uniaxial puede ser 0.',
  Vu_x:
    'Cortante factorado en X (kgf). Sale del menor entre: (a) cortante por capacidad — E.060 21.4.3.1.a — basado en Mn de la columna y H libre; y (b) 2.5 veces el cortante por sismo — E.060 21.4.3.1.b. Si lo dejas en 0 no se chequea cortante.',
  Vu_y: 'Cortante factorado en Y (kgf). Mismo criterio que Vu_x.',
  loadsDisclaimer:
    'Los valores precargados son SOLO EJEMPLO para que la app tenga algo que mostrar. Reemplazalos con los de tu analisis estructural. AlphaConcreto verifica una seccion contra cargas dadas, NO realiza el analisis estructural global de la edificacion (sismo, gravedad, combinaciones).',

  // Reinforcement (convencion profesional: esquinas + intermedias)
  diam_corners:
    'Diametro de las 4 barras de esquina. En seccion rectangular siempre hay 4 esquinas. Tipico: 5/8", 3/4", 1". Suele coincidir con el diametro mayor cuando hay variacion.',
  n_intermediate_L1:
    'Numero de barras INTERMEDIAS entre las dos esquinas en cada cara horizontal (la cantidad se repite en la cara superior y la inferior). 0 si solo quieres armado en esquinas. Total agregado = 2 x este numero.',
  diam_intermediate_L1:
    'Diametro de las barras intermedias L1. Puede ser distinto al de esquina (es comun colocar laterales un poco menores).',
  n_intermediate_L2:
    'Numero de barras INTERMEDIAS entre las dos esquinas en cada cara vertical (izquierda y derecha). Total agregado = 2 x este numero.',
  diam_intermediate_L2: 'Diametro de las barras intermedias L2.',
  stirrup_diam:
    'Diametro del estribo (zuncho). En Peru es comun 3/8" (E.060 21.4.4 exige minimo 3/8" para columnas en zonas de alto sismo).',
  totalBars:
    'Total de barras = 4 (esquinas) + 2 x n_intermediate_L1 + 2 x n_intermediate_L2.',

  // Outputs
  Ag: 'Area gruesa de la seccion = L1 x L2. Es el area total de concreto antes de descontar el acero.',
  As: 'Area total de acero longitudinal sumando todas las barras. Compara contra As_min y As_max.',
  As_min:
    'Area minima de acero permitida = 1% Ag. Si As < As_min, la seccion esta sobredimensionada en concreto. E.060 21.4.5.',
  As_max:
    'Area maxima de acero permitida = 6% Ag. Si As > As_max, hay congestionamiento de barras y se debe agrandar la seccion. E.060 21.4.5.',
  rho: 'Cuantia de refuerzo longitudinal = As / Ag. Limites: 1% <= rho <= 6%.',
  n_bars: 'Cantidad total de barras longitudinales en la seccion (suma de las cuatro caras, sin contar dos veces las esquinas).',
  bars_tag:
    'Resumen del armado, agrupando por diametro. Por ejemplo "8 of 3/4" + 4 of 1\\""  significa 8 barras de 3/4 pulgada mas 4 barras de 1 pulgada.',
  dc_ratio:
    'Ratio Demanda / Capacidad. Si <= 1.0, la seccion resiste las cargas factoradas con el factor phi aplicado. Mayor a 1.0 = sobreesfuerzo, hay que aumentar As o agrandar seccion.',

  // Analysis
  pmSurface:
    'Superficie tridimensional que envuelve todas las combinaciones de carga axial P y momentos Mx, My que la columna puede resistir. Cualquier punto (Pu, Mux, Muy) dentro del solido es una demanda admisible; fuera de el, la columna falla.',
  pmCutX:
    'Corte plano de la superficie 3D para Mu_y = 0. Sirve para verificar columnas que solo flexionan en el eje X (sismo X dominante).',
  pmCutY: 'Corte plano para Mu_x = 0. Verificacion de flexion alrededor del eje Y (sismo Y dominante).',
} as const;

export type HelpKey = keyof typeof HELP;
